import { type Flags } from '@core/context/FeatureFlagsContext';
import { MonarchEditableAsset } from '@core/context/types';
import { type BlockPage } from '@core/schemas/schema';

export type QuestionInjectionConfig = { insertPosition?: string; replacePosition?: string; pages: BlockPage[] };
export type InjectionKey = 'EMCC' | 'EAB_GRAD' | 'EAB_UNDERGRAD' | 'noPiiTest' | keyof Flags;

export type QuestionInjectionSchema = {
  [key in InjectionKey]?: QuestionInjectionConfig[];
};

const getQuestionInjectionSchema = (flags: Flags): QuestionInjectionSchema => ({
  noPiiTest: [
    {
      replacePosition: '/zip',
      pages: [
        {
          slug: (flags?.noPii as MonarchEditableAsset)?.questionSlug ?? '/zip',
          stepNumber: 4.6,
          questionId: 'editable-asset-personalization-zip-code',
          overrideNextStep: '/results',
        },
      ],
    },
  ],
  securityBoost: [
    {
      replacePosition: '/contact-info',
      pages: [
        {
          slug: flags?.securityBoost?.questionSlug ?? '/contact-info',
          stepNumber: 5.6,
          questionId: 'editable-asset-email-phone-security-boost',
          overrideNextStep: '/results',
        },
      ],
    },
  ],
  personalizationQuestionImportantFactors: [
    {
      insertPosition: '/level-of-education',
      pages: [
        {
          slug:
            (flags?.personalizationQuestionImportantFactors as MonarchEditableAsset).questionSlug ??
            '/important-factors',
          stepNumber: 1.5,
          questionId: 'editable-asset-personalization-important-factors',
        },
      ],
    },
  ],
  personalizationQuestionPursuingHigherEducation: [
    {
      // If the important factors question is also active, insert this question after it.
      insertPosition: flags?.personalizationQuestionImportantFactors
        ? (flags?.personalizationQuestionImportantFactors as MonarchEditableAsset).questionSlug
        : '/level-of-education',
      pages: [
        {
          slug:
            (flags?.personalizationQuestionPursuingHigherEducation as MonarchEditableAsset).questionSlug ??
            '/pursuing-higher-education',
          stepNumber: 1.5,
          questionId: 'editable-asset-personalization-pursuing-higher-education',
        },
      ],
    },
  ],
  personalizationQuestionLevelOfEducation: [
    {
      replacePosition: '/level-of-education',
      pages: [
        {
          slug: '/level-of-education',
          stepNumber: 1.5,
          questionId: 'editable-asset-personalization-level-of-education',
        },
      ],
    },
  ],
  EMCC: [
    {
      replacePosition: '/name',
      pages: [
        {
          slug: '/name',
          stepNumber: 5.5,
          questionId: '1004-name-emcc-test',
        },

        flags?.securityBoost
          ? {
              slug: flags?.securityBoost?.questionSlug ?? '/contact-info',
              stepNumber: 5.6,
              questionId: 'editable-asset-email-phone-security-boost-emcc',
              overrideNextStep: '/results',
            }
          : {
              slug: '/contact-info-emcc',
              stepNumber: 5.6,
              questionId: '1032-email-phone-emcc',
              overrideNextStep: '/results',
            },
      ],
    },
    {
      replacePosition: '/zip',
      pages: [
        {
          slug: '/zip',
          stepNumber: 4.6,
          questionId: '1003-zip-code-emcc-test',
        },
      ],
    },
  ],
  EAB_UNDERGRAD: [
    {
      insertPosition: '/level-of-education',
      pages: [
        {
          slug: '/transfer-student',
          stepNumber: 1.5,
          questionId: '1031-transfer-student',
        },
      ],
    },
    {
      replacePosition: '/zip',
      pages: [
        {
          slug: '/zip',
          stepNumber: 4.6,
          questionId: '1003-zip-code-eab-test',
        },
      ],
    },
    {
      replacePosition: '/name',
      pages: [
        {
          slug: '/name',
          stepNumber: 5.5,
          questionId: '1004-name-eab-test',
        },
        flags?.securityBoost
          ? {
              slug: flags?.securityBoost?.questionSlug ?? '/contact-info',
              stepNumber: 5.6,
              questionId: 'editable-asset-email-phone-security-boost-eab',
              overrideNextStep: '/results',
            }
          : {
              slug: '/contact-info-eab',
              stepNumber: 5.5,
              questionId: '1027-email-phone-is-eab-undergrad',
              overrideNextStep: '/results',
            },
      ],
    },
  ],
  EAB_GRAD: [
    {
      replacePosition: '/zip',
      pages: [
        {
          slug: '/zip',
          stepNumber: 4.6,
          questionId: '1003-zip-code-eab-test',
        },
      ],
    },
    {
      replacePosition: '/name',
      pages: [
        {
          slug: '/name',
          stepNumber: 5.5,
          questionId: '1004-name-eab-test',
        },
        flags?.securityBoost
          ? {
              slug: flags?.securityBoost?.questionSlug ?? '/contact-info',
              stepNumber: 5.6,
              questionId: 'editable-asset-email-phone-security-boost-eab',
              overrideNextStep: '/results',
            }
          : {
              slug: '/contact-info-eab',
              stepNumber: 5.5,
              questionId: '1027-email-phone-is-eab-grad',
              overrideNextStep: '/results',
            },
      ],
    },
  ],
});
export default getQuestionInjectionSchema;
